// combatpro/encounter/useEncounterPersistence.js
//
// Combat PRO - Encounter Persistence Runtime (Stage 116: orchestration only).
//
// A dedicated React hook that owns the persistence ORCHESTRATION migrated from the
// legacy vi* autosave engine (battletracker.html 10501-10555): dirty tracking,
// the autosave signature diff, the save-status lifecycle, autosave scheduling, and
// the debounce timers. It decides WHEN persistence should occur; it never decides
// HOW. There is exactly one integration point - the onPersist callback seam -
// where Stage 117 attaches the approved storage medium.
//
// Per the Stage 116 decision (Option B), this hook owns the useEffect lifecycle,
// setInterval, and debounce timers so that CombatContext stays free of any
// persistence useEffect. The hook is engine- and medium-agnostic: the current
// signature and the capture payload are supplied by the caller as accessors
// (getSignature / getCaptured), so the hook reuses buildSignature /
// captureEncounterCombat WITHOUT importing the engine or CombatContext internals.
//
// FORBIDDEN here and absent (deferred): localStorage / sessionStorage, IndexedDB,
// saveToCloud / loadFromCloud, saveState / loadState, any storage adapter, boot,
// dashboard CRUD / switching / rendering, and the encounterStore population. No DOM
// beyond the timer lifecycle. No optional chaining. No nullish coalescing. No em
// dash. No literal em dash in source.
//
// Source mapping (battletracker.html):
//   - viSetSave (10523)        -> setSave: status transition + lastSavedAt stamp on
//                                'saved'. States: 'saved' | 'saving' | 'unsaved'.
//   - viMarkDirty (10531)      -> markDirty: optional Empty->Design promotion
//                                (injected onPromoteFromEmpty), status 'unsaved',
//                                debounce (dirtyDebounceMs) -> flushSave.
//   - viFlushSave (10538)      -> flushSave: 'saving' -> [onPersist seam] -> 'saved'.
//                                The legacy viSyncActiveFromLive capture is supplied
//                                via getCaptured and handed to the seam; NO storage
//                                is performed here.
//   - viPersistImmediate (10508) -> persistImmediate: immediate flush (no debounce).
//   - viSig (10516)            -> reused via getSignature (buildSignature in the
//                                caller); the hook only diffs it.
//   - viAutoTick (10546)       -> the interval body: archived-skip (injected
//                                isArchived), seed lastSig on first tick, diff, and
//                                markDirty on change.
//   - viStartAutoSave (10555)  -> the useEffect: seed lastSig, start
//                                setInterval(autoTick, autoTickMs) once, clear on
//                                unmount (the React-idiomatic "start once" guard).
//
// The legacy 800ms saveToCloud debounce inside viPersist is a MEDIUM-timing detail
// and is intentionally NOT reproduced here; the seam (Stage 117) may debounce the
// medium write internally. This hook only schedules the orchestration.

import { useState, useRef, useEffect, useCallback } from 'react';

export function useEncounterPersistence(options) {
  var opts = options || {};
  var autoTickMs = (typeof opts.autoTickMs === 'number') ? opts.autoTickMs : 1500;
  var dirtyDebounceMs = (typeof opts.dirtyDebounceMs === 'number') ? opts.dirtyDebounceMs : 1000;
  var enabled = (opts.enabled === undefined) ? true : !!opts.enabled;

  var saveStateHook = useState('saved');
  var saveState = saveStateHook[0];
  var setSaveState = saveStateHook[1];

  var dirtyHook = useState(false);
  var isDirty = dirtyHook[0];
  var setIsDirty = dirtyHook[1];

  var savedAtHook = useState(null);
  var lastSavedAt = savedAtHook[0];
  var setLastSavedAt = savedAtHook[1];

  // Refs. optsRef is refreshed every render so the interval (which is created once)
  // always reads the LATEST caller accessors - the standard React pattern that
  // avoids a stale closure without restarting the timer on every state change.
  var optsRef = useRef(opts);
  optsRef.current = opts;
  var lastSigRef = useRef(null);      // viLastSig
  var dirtyTimerRef = useRef(null);   // viDirtyTimer

  // viSetSave (10523): set status; stamp lastSavedAt when transitioning to 'saved'.
  var setSave = useCallback(function (s) {
    setSaveState(s);
    if (s === 'saved') { setLastSavedAt(Date.now()); }
  }, [setSaveState, setLastSavedAt]);

  // viFlushSave (10538): 'saving' -> [seam] -> 'saved'. Storage is NOT performed;
  // onPersist is the single seam and receives the captured payload (getCaptured).
  // Status always settles to 'saved' (legacy resolved both branches to 'saved').
  var flushSave = useCallback(function () {
    var o = optsRef.current || {};
    setSave('saving');
    setIsDirty(false);
    var captured = (typeof o.getCaptured === 'function') ? o.getCaptured() : null;
    var seam = (typeof o.onPersist === 'function') ? o.onPersist : function () {};
    Promise.resolve()
      .then(function () { return seam(captured); })
      .then(function () { setSave('saved'); }, function () { setSave('saved'); });
  }, [setSave, setIsDirty]);
  var flushRef = useRef(flushSave);
  flushRef.current = flushSave;

  // viMarkDirty (10531): optional Empty->Design promotion (injected; store coupling
  // deferred), status 'unsaved', debounce -> flushSave.
  var markDirty = useCallback(function () {
    var o = optsRef.current || {};
    if (typeof o.onPromoteFromEmpty === 'function') { o.onPromoteFromEmpty(); }
    setSave('unsaved');
    setIsDirty(true);
    if (dirtyTimerRef.current) { clearTimeout(dirtyTimerRef.current); }
    dirtyTimerRef.current = setTimeout(function () { flushRef.current(); }, dirtyDebounceMs);
  }, [setSave, setIsDirty, dirtyDebounceMs]);
  var markDirtyRef = useRef(markDirty);
  markDirtyRef.current = markDirty;

  // viPersistImmediate (10508): immediate flush, no debounce.
  var persistImmediate = useCallback(function () {
    flushRef.current();
  }, []);

  // viStartAutoSave (10555) + viAutoTick (10546): the timer lifecycle lives here so
  // CombatContext stays persistence-useEffect-free. Seed lastSig, start the interval
  // once, clear it (and any pending debounce) on unmount.
  useEffect(function () {
    if (!enabled) { return undefined; }
    try {
      var o0 = optsRef.current || {};
      lastSigRef.current = (typeof o0.getSignature === 'function') ? o0.getSignature() : null;
    } catch (e0) {
      lastSigRef.current = null;
    }

    var id = setInterval(function () {
      var o = optsRef.current || {};
      // viAutoTick archived-skip (injected predicate; defaults to never-skip until
      // the store exists in the Dashboard stage).
      if (typeof o.isArchived === 'function' && o.isArchived()) { return; }
      var sig;
      try {
        sig = (typeof o.getSignature === 'function') ? o.getSignature() : null;
      } catch (e1) {
        sig = null;
      }
      if (lastSigRef.current === null) {
        lastSigRef.current = sig;
      } else if (sig !== lastSigRef.current) {
        lastSigRef.current = sig;
        markDirtyRef.current();
      }
    }, autoTickMs);

    return function () {
      clearInterval(id);
      if (dirtyTimerRef.current) {
        clearTimeout(dirtyTimerRef.current);
        dirtyTimerRef.current = null;
      }
    };
  }, [enabled, autoTickMs]);

  return {
    saveState: saveState,
    isDirty: isDirty,
    lastSavedAt: lastSavedAt,
    markDirty: markDirty,
    flushSave: flushSave,
    persistImmediate: persistImmediate
  };
}
